import React from "react";
import { Calendar, ConfigProvider, Select, Input, InputNumber, Button, Transfer, Checkbox } from "antd";
import type { CalendarProps, TransferProps } from "antd";
import type { Dayjs } from "dayjs";
import esES from "antd/es/locale/es_ES";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
  getCelebrityDays,
  type CelebrityDayData,
} from "../../services/celebrity-days.service.ts";
import {
  getHolidayBonuses,
  createHolidayBonus,
  updateHolidayBonus,
  deleteHolidayBonusByDate,
  getHolidayBonusByDate,
  type HolidayBonusData,
  type HolidayBonusPriceData,
} from "../../services/holiday-bonus.service.ts";
import { readPositions } from "../../services/position.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";
import DeleteCelebrityDay from "./components/DeleteCelebrityDay.tsx";

dayjs.locale("es");

const { Option } = Select;

interface Position {
  id: number;
  name: string;
}

const CelebrityDaysPage = () => {
  const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(null);
  const [currentMonth, setCurrentMonth] = React.useState<Dayjs>(dayjs());
  const [celebrityDays, setCelebrityDays] = React.useState(
    [] as CelebrityDayData[]
  );
  const [holidayBonuses, setHolidayBonuses] = React.useState(
    [] as HolidayBonusData[]
  );
  const [positions, setPositions] = React.useState<Position[]>([]);
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState("");
  const [modalPrices, setModalPrices] = React.useState<HolidayBonusPriceData[]>([
    { amount: 0, positionIds: [] },
  ]);
  const [editingBonus, setEditingBonus] = React.useState<HolidayBonusData | null>(
    null
  );
  const [editingLegacyDay, setEditingLegacyDay] = React.useState<CelebrityDayData | null>(
    null
  );
  const [loading, setLoading] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [dayToDelete, setDayToDelete] = React.useState<CelebrityDayData | null>(
    null
  );
  const [activeGroupIndex, setActiveGroupIndex] = React.useState<number>(0);
  const [selectedPositionIds, setSelectedPositionIds] = React.useState<number[]>([]);
  const [searchText, setSearchText] = React.useState<string>("");
  useAppSettings();

  const refreshDays = async () => {
    try {
      setLoading(true);
      const [celebrityResp, holidayResp, positionsResp] = await Promise.all([
        getCelebrityDays(),
        getHolidayBonuses(),
        readPositions(),
      ]);
      setCelebrityDays(celebrityResp?.days || []);
      setHolidayBonuses(holidayResp?.bonuses || []);
      setPositions(positionsResp || []);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    refreshDays();
  }, []);


  React.useEffect(() => {
    setSelectedPositionIds([]);
    setSearchText("");
  }, [activeGroupIndex]);


  React.useEffect(() => {
    if (!isModalVisible) {
      setSelectedPositionIds([]);
      setSearchText("");
    }
  }, [isModalVisible]);

  const handleDateSelect = async (date: Dayjs) => {

    if (
      date &&
      date.isValid() &&
      date.month() === currentMonth.month() &&
      date.year() === currentMonth.year()
    ) {
      setSelectedDate(date);
      const yyyymmdd = date.format("YYYY-MM-DD");


      const existingLegacyDay = celebrityDays.find((day) => day.date === yyyymmdd);
      if (existingLegacyDay) {

        setEditingLegacyDay(existingLegacyDay);
        setEditingBonus(null);
        setIsModalVisible(true);
        return;
      }


      const existingBonus = await getHolidayBonusByDate(yyyymmdd);
      if (existingBonus) {

        setEditingBonus(existingBonus);
        setEditingLegacyDay(null);
        setModalTitle(existingBonus.title);
        setModalPrices(existingBonus.prices || [{ amount: 0, positionIds: [] }]);
        setActiveGroupIndex(0);
        setIsModalVisible(true);
      } else {

        setEditingBonus(null);
        setEditingLegacyDay(null);
        setModalTitle("");
        setModalPrices([{ amount: 0, positionIds: [] }]);
        setActiveGroupIndex(0);
        setIsModalVisible(true);
      }
    }
  };

  const handleAddGroup = () => {
    const newGroupIndex = modalPrices.length;

    const newGroup = {
      amount: 0,
      positionIds: selectedPositionIds.length > 0 ? [...selectedPositionIds] : []
    };


    if (selectedPositionIds.length > 0) {
      const alreadyAssigned = modalPrices.some((price) =>
        selectedPositionIds.some(id => price.positionIds.includes(id))
      );

      if (alreadyAssigned) {
        notification(
          "Error",
          "Uno o más puestos seleccionados ya están asignados a otro grupo. Por favor, quítalos del otro grupo primero.",
          "danger"
        );
        return;
      }
    }

    setModalPrices([...modalPrices, newGroup]);

    setActiveGroupIndex(newGroupIndex);

    if (selectedPositionIds.length > 0) {
      setSelectedPositionIds([]);
      setSearchText("");
    }
  };

  const handleRemoveGroup = (index: number) => {
    const newPrices = modalPrices.filter((_, i) => i !== index);
    setModalPrices(newPrices);


    if (newPrices.length === 0) {

      setModalPrices([{ amount: 0, positionIds: [] }]);
      setActiveGroupIndex(0);
    } else {
      if (activeGroupIndex === index) {
        setActiveGroupIndex(Math.max(0, newPrices.length - 1));
      } else if (activeGroupIndex > index) {
        setActiveGroupIndex(activeGroupIndex - 1);
      }
    }
  };

  const handleGroupAmountChange = (index: number, amount: number | null) => {
    const newPrices = [...modalPrices];
    newPrices[index] = {
      ...newPrices[index],
      amount: amount || 0,
    };
    setModalPrices(newPrices);
  };

  const handleGroupAmountFocus = (index: number) => {
    const currentPrice = modalPrices[index];
    if (currentPrice.amount === 0) {
      const newPrices = [...modalPrices];
      newPrices[index] = {
        ...newPrices[index],
        amount: null as any,
      };
      setModalPrices(newPrices);
    }
  };

  const handleRemovePositionFromGroup = (groupIndex: number, positionId: number) => {
    let newPrices = [...modalPrices];
    newPrices[groupIndex] = {
      ...newPrices[groupIndex],
      positionIds: newPrices[groupIndex].positionIds.filter((id) => id !== positionId),
    };


    if (newPrices[groupIndex].positionIds.length === 0) {
      newPrices = newPrices.filter((_, idx) => idx !== groupIndex);


      if (newPrices.length === 0) {
        newPrices = [{ amount: 0, positionIds: [] }];
        setActiveGroupIndex(0);
      } else {

        if (activeGroupIndex === groupIndex) {
          setActiveGroupIndex(Math.max(0, newPrices.length - 1));
        } else if (activeGroupIndex > groupIndex) {
          setActiveGroupIndex(activeGroupIndex - 1);
        }
      }
    }

    setModalPrices(newPrices);
  };

  const handleTransferChange = (
    targetKeys: React.Key[],
    direction: 'left' | 'right',
    moveKeys: React.Key[]
  ) => {
    let newPrices = [...modalPrices];

    if (direction === 'right') {

      const currentGroup = newPrices[activeGroupIndex];
      const newPositionIds = moveKeys.map((key) => Number(String(key)));


      const alreadyAssigned = newPrices.some((price, idx) =>
        idx !== activeGroupIndex &&
        newPositionIds.some(id => price.positionIds.includes(id))
      );

      if (alreadyAssigned) {
        notification(
          "Error",
          "Uno o más puestos ya están asignados a otro grupo. Por favor, quítalos del otro grupo primero.",
          "danger"
        );
        return;
      }

      newPrices[activeGroupIndex] = {
        ...currentGroup,
        positionIds: [...new Set([...currentGroup.positionIds, ...newPositionIds])],
      };
    } else {

      const positionIdsToRemove = moveKeys.map((key) => Number(String(key)));

      newPrices = newPrices.map((price) => ({
        ...price,
        positionIds: price.positionIds.filter(id => !positionIdsToRemove.includes(id)),
      }));


      const groupsWithPositions = newPrices.filter((price) => price.positionIds.length > 0);


      if (groupsWithPositions.length < newPrices.length) {
        let newActiveIndex = activeGroupIndex;


        let groupsBeforeActive = 0;
        for (let i = 0; i < activeGroupIndex; i++) {
          if (newPrices[i].positionIds.length > 0) {
            groupsBeforeActive++;
          }
        }

        newActiveIndex = groupsBeforeActive;
        if (newActiveIndex >= groupsWithPositions.length) {
          newActiveIndex = Math.max(0, groupsWithPositions.length - 1);
        }


        if (groupsWithPositions.length === 0) {
          newPrices = [{ amount: 0, positionIds: [] }];
          setActiveGroupIndex(0);
        } else {
          setActiveGroupIndex(newActiveIndex);
          newPrices = groupsWithPositions;
        }
      }
    }

    setModalPrices(newPrices);
  };


  const getAvailablePositions = () => {
    const allAssignedIds = new Set(
      modalPrices.flatMap((price) => price.positionIds)
    );
    return positions.filter((position) => !allAssignedIds.has(position.id));
  };


  const getAllAssignedPositions = () => {
    const allAssignedIds = new Set(
      modalPrices.flatMap((price) => price.positionIds)
    );
    return positions.filter((position) => allAssignedIds.has(position.id));
  };

  const getTransferDataSource = () => {
    return getAvailablePositions().map((position) => ({
      key: String(position.id),
      title: position.name,
    }));
  };

  const getSelectedKeys = () => {
    const allAssignedIds = new Set(
      modalPrices.flatMap((price) => price.positionIds)
    );
    return positions
      .filter((position) => allAssignedIds.has(position.id))
      .map((position) => String(position.id));
  };

  const handleModalOk = async () => {
    if (!selectedDate || !modalTitle) {
      notification("Error", "Por favor completa el título", "danger");
      return;
    }

    try {
      setLoading(true);
      const yyyymmdd = selectedDate.format("YYYY-MM-DD");


      const cleanPrices = modalPrices
        .filter((price) => {

          return price.positionIds.length > 0 && price.amount && price.amount > 0;
        })
        .map((price) => ({
          amount: Number(price.amount) || 0,
          positionIds: price.positionIds,
        }));



      if (cleanPrices.length === 0 && !editingBonus) {
        notification(
          "Error",
          "Debes crear al menos un grupo con puestos asignados y un monto mayor a 0",
          "danger"
        );
        setLoading(false);
        return;
      }

      if (editingBonus) {

        await updateHolidayBonus(editingBonus.id, {
          title: modalTitle,
          prices: cleanPrices,
        });
        notification("Éxito", "Bono actualizado correctamente", "success");
      } else {

        await createHolidayBonus({
          date: yyyymmdd,
          title: modalTitle,
          prices: cleanPrices,
        });
        notification("Éxito", "Bono creado correctamente", "success");
      }

      await refreshDays();
      setIsModalVisible(false);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Error al procesar la solicitud";
      notification("Error", errorMessage, "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (date: string) => {
    try {
      setLoading(true);
      await deleteHolidayBonusByDate(date);
      notification("Éxito", "Bono eliminado correctamente", "success");
      await refreshDays();
      setIsModalVisible(false);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Error al eliminar el bono";
      notification("Error", errorMessage, "danger");
    } finally {
      setLoading(false);
    }
  };

  const dateCellRender = (value: Dayjs) => {
    const yyyymmdd = value.format("YYYY-MM-DD");
    const celebrityDay = celebrityDays.find((day) => day.date === yyyymmdd);
    const holidayBonus = holidayBonuses.find((bonus) => bonus.date === yyyymmdd);

    if (celebrityDay) {

      return (
        <div
          className="events"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDateSelect(value);
          }}
          style={{ cursor: "pointer" }}
          title={`${celebrityDay.title} (Legacy - Solo lectura)`}
        >
          <div
            style={{
              backgroundColor: "#fff3cd",
              color: "#856404",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "12px",
              border: "1px solid #ffc107",
              margin: "2px 0",
            }}
          >
            {celebrityDay.title} (Legacy)
          </div>
        </div>
      );
    }

    if (holidayBonus) {

      return (
        <div
          className="events"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDateSelect(value);
          }}
          style={{ cursor: "pointer" }}
          title={holidayBonus.title}
        >
          <div
            style={{
              backgroundColor: "#d4edda",
              color: "#155724",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "12px",
              border: "1px solid #28a745",
              margin: "2px 0",
            }}
          >
            {holidayBonus.title}
          </div>
        </div>
      );
    }

    return null;
  };

  const cellRender: CalendarProps<Dayjs>["cellRender"] = (current, info) => {
    if (info.type === "date") return dateCellRender(current);
    return info.originNode;
  };

  const isLegacyDay = editingLegacyDay !== null;
  const isEditing = editingBonus !== null;

  return (
    <Layout>
      <Title title="Días Festivos" />
      <div className="container-fluid">
        <ConfigProvider locale={esES}>
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Días Festivos</h5>
                  <p className="text-muted mb-0">
                    Selecciona una fecha en el calendario para agregar o editar un
                    bono. Los días marcados como "Legacy" son de solo lectura.
                  </p>
                </div>
                <div className="card-body">
                  <Calendar
                    cellRender={cellRender}
                    onSelect={handleDateSelect}
                    style={{ width: "100%", margin: "10px auto" }}
                    mode="month"
                    onPanelChange={(value, mode) => {
                      setSelectedDate(null);
                      setCurrentMonth(value);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </ConfigProvider>

        <ModalContainer
          open={isModalVisible}
          title={
            isLegacyDay
              ? `Día Festivo (Legacy - Solo Lectura) - ${selectedDate ? selectedDate.format("DD/MM/YYYY") : ""}`
              : isEditing
                ? `Editar Bono - ${selectedDate ? selectedDate.format("DD/MM/YYYY") : ""}`
                : `Agregar Bono - ${selectedDate ? selectedDate.format("DD/MM/YYYY") : ""}`
          }
          size="lg"
          closeFn={() => {
            setIsModalVisible(false);
            setEditingBonus(null);
            setEditingLegacyDay(null);
            setSelectedPositionIds([]);
            setSearchText("");
          }}
        >
          {isLegacyDay ? (
            <div>
              <div className="alert alert-warning">
                <i className="fa fa-exclamation-triangle me-2"></i>
                Este es un día festivo del sistema anterior (Legacy). Solo puedes
                visualizarlo, no puedes editarlo.
              </div>
              <div className="mb-3">
                <label className="form-label">Título</label>
                <Input
                  value={editingLegacyDay?.title || ""}
                  disabled
                  className="form-control"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Tipo de Bono (Legacy)</label>
                <Input
                  value={editingLegacyDay?.bonusType || ""}
                  disabled
                  className="form-control"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="mb-3">
                <label className="form-label">Título del Bono</label>
                <Input
                  value={modalTitle}
                  onChange={(e) => setModalTitle(e.target.value)}
                  placeholder="Nombre"
                  className="form-control"
                />
              </div>

              {/* Lista de puestos disponibles con checkboxes */}
              {modalPrices.length > 0 && (
                <div className="mb-2">
                  <label className="form-label mb-1">
                    Asignar Puestos (Los puestos se agregarán al Grupo {activeGroupIndex + 1})
                  </label>
                  <div className="border rounded p-2" style={{ backgroundColor: '#fafafa' }}>
                    <div className="mb-2">
                      <Input
                        placeholder="Buscar puestos..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        prefix={<i className="fa fa-search" />}
                        size="small"
                      />
                    </div>
                    <div
                      style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        padding: '6px',
                        backgroundColor: '#fff',
                      }}
                    >
                      {getAvailablePositions()
                        .filter((position) =>
                          position.name.toLowerCase().includes(searchText.toLowerCase())
                        )
                        .map((position) => (
                          <div
                            key={position.id}
                            style={{
                              padding: '4px 8px',
                              borderBottom: '1px solid #f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Checkbox
                              checked={modalPrices[activeGroupIndex]?.positionIds.includes(position.id) || false}
                              onChange={(e) => {
                                if (e.target.checked) {

                                  handleTransferChange(
                                    [],
                                    'right',
                                    [String(position.id)]
                                  );
                                } else {

                                  handleTransferChange(
                                    [],
                                    'left',
                                    [String(position.id)]
                                  );
                                }
                              }}
                            >
                              {position.name}
                            </Checkbox>
                          </div>
                        ))}
                      {getAvailablePositions().filter((position) =>
                        position.name.toLowerCase().includes(searchText.toLowerCase())
                      ).length === 0 && (
                          <div className="text-center text-muted p-3">
                            No hay puestos disponibles
                          </div>
                        )}
                    </div>
                    <div className="mt-2 d-flex justify-content-end align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleAddGroup}
                      >
                        <i className="fa fa-plus me-2"></i>Nuevo Grupo
                      </button>
                      {modalPrices.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => {
                            if (selectedPositionIds.length > 0) {
                              handleTransferChange(
                                [],
                                'right',
                                selectedPositionIds.map((id) => String(id))
                              );
                              setSelectedPositionIds([]);
                              setSearchText("");
                            }
                          }}
                          disabled={selectedPositionIds.length === 0}
                        >
                          <i className="fa fa-plus me-2"></i>
                          Agregar ({selectedPositionIds.length})
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Contenedor de grupos en grid */}
              {modalPrices.length > 0 && (
                <div className="row g-3">
                  {modalPrices.map((price, index) => (
                    <div key={index} className="col-12 col-md-6 col-lg-4">
                      <div
                        className="border rounded p-3 h-100"
                        style={{
                          backgroundColor: activeGroupIndex === index ? "#e7f3ff" : "#f8f9fa",
                          border: activeGroupIndex === index ? "2px solid #0d6efd" : "1px solid #dee2e6",
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => setActiveGroupIndex(index)}
                      >
                        <div className="d-flex justify-content-end mb-2">
                          {modalPrices.length > 1 ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveGroup(index);
                              }}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveGroup(index);
                              }}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          )}
                        </div>

                        <div className="mb-2">
                          <label className="form-label mb-1">Monto del Grupo ($)</label>
                          <InputNumber
                            value={price.amount === 0 ? null : price.amount}
                            onChange={(value) =>
                              handleGroupAmountChange(index, value)
                            }
                            onFocus={() => handleGroupAmountFocus(index)}
                            placeholder="0.00"
                            min={0}
                            step={0.01}
                            style={{ width: "100%" }}
                            formatter={(value) =>
                              value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                            }
                            parser={(value) => {

                              const parsed = value!.replace(/\$\s?|(,*)/g, "").replace(/[^\d.]/g, "");

                              const parts = parsed.split(".");
                              if (parts.length > 2) {
                                return parts[0] + "." + parts.slice(1).join("");
                              }
                              return parsed as any;
                            }}
                            onKeyPress={(e) => {

                              const char = String.fromCharCode(e.which || e.keyCode);
                              if (!/[0-9.]/.test(char) && !/[0-9]/.test(e.key) && e.key !== "." && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Tab") {
                                e.preventDefault();
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>

                        {/* Mostrar puestos asignados a este grupo */}
                        <div>
                          <label className="form-label mb-1">Puestos en este grupo</label>
                          {price.positionIds.length === 0 ? (
                            <div className="text-muted p-2 border rounded" style={{ backgroundColor: "#fff" }}>
                              No hay puestos asignados. Selecciona puestos arriba y haz clic en "Agregar".
                            </div>
                          ) : (
                            <div
                              style={{
                                maxHeight: '120px',
                                overflowY: 'auto',
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px',
                                padding: '6px',
                                backgroundColor: '#fff',
                              }}
                            >
                              {price.positionIds.map((positionId) => {
                                const position = positions.find((p) => p.id === positionId);
                                return position ? (
                                  <div
                                    key={positionId}
                                    style={{
                                      padding: '4px 8px',
                                      borderBottom: '1px solid #f0f0f0',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Checkbox
                                      checked={true}
                                      onChange={(e) => {
                                        if (!e.target.checked) {
                                          handleRemovePositionFromGroup(index, positionId);
                                        }
                                      }}
                                    >
                                      {position.name}
                                    </Checkbox>
                                  </div>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {modalPrices.length === 0 && (
                <div className="alert alert-info">
                  <i className="fa fa-info-circle me-2"></i>
                  Haz clic en "Nuevo Grupo" para comenzar a asignar puestos.
                </div>
              )}

              {/* Footer con botones */}
              <div className="d-flex justify-content-end gap-2 mt-3 pt-2 border-top">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setIsModalVisible(false);
                    setEditingBonus(null);
                    setEditingLegacyDay(null);
                    setSelectedPositionIds([]);
                    setSearchText("");
                  }}
                >
                  Cancelar
                </button>
                {isEditing && (
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      if (selectedDate) {
                        handleDeleteClick(selectedDate.format("YYYY-MM-DD"));
                      }
                    }}
                  >
                    <i className="fa fa-trash me-2"></i>Eliminar
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={handleModalOk}
                  disabled={
                    !selectedDate ||
                    !modalTitle ||
                    modalPrices.length === 0 ||
                    modalPrices.some(
                      (p) => !p.amount || p.amount <= 0
                    )
                  }
                >
                  {isEditing ? "Actualizar" : "Crear"}
                </button>
              </div>
            </>
          )}
        </ModalContainer>

        <ModalContainer
          open={showDeleteModal}
          title="Eliminar día festivo"
          closeFn={() => setShowDeleteModal(false)}
          children={
            dayToDelete && (
              <DeleteCelebrityDay
                closeFn={() => setShowDeleteModal(false)}
                refreshFn={refreshDays}
                celebrityDay={dayToDelete}
                setLoading={setLoading}
              />
            )
          }
        />
      </div>
    </Layout>
  );
};

export default CelebrityDaysPage;
